import Vue from 'vue'
import App from './App.vue'
import {createStore} from './store'
import {createRouter} from './router'
import {createVuetify} from './vuetify'
import {createI18n} from './i18n'
import {sync} from 'vuex-router-sync';
import addPropertiesToApiData from './api/addPropertiesToApiData';
import Meta from 'vue-meta';

Vue.use(Meta, {ssrAppId: 1});

import VueToast from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';
import {showToastDuration, appHost} from '../config/app';

Vue.use(VueToast, {
    position: "top-right",
    duration: showToastDuration,
});

import VueLazyLoad from 'vue-lazyload';

Vue.use(VueLazyLoad, {});

const {defaultLanguage, appLocales} = require('../config/localization');
const {expireDays} = require('../config/cookie');
const cookieNames = require('./interfaces/cookieNamesInterface');

import {routesNames} from "./interfaces/routeInterface";
import {hashRoutes} from "../config/url";
import referralDataTypes from "./interfaces/referralDataTypesInterface";

Vue.use(require('vue-cookies'));
Vue.$cookies.config(expireDays.sessionKey + 'd', '/', appHost, '', '');

// register components
require('./vue/registerComponents')(Vue);

void require('./chat/index').registerChat();

// Expose a factory function that creates a fresh set of store, router,
// app instances on each call (which is called for each SSR request)
export function createApp(context) {
    return new Promise((resolve, reject) => {
        // create store
        const store = createStore();

        // prepare initial storage data
        prepareInitialState(context, store)
            .then(() => {
                // create router instance
                const router = createRouter(store);

                // create vuetify
                const vuetify = createVuetify();

                // create i18n
                const i18n = createI18n();

                void require('./util/analytics/index').initAnalytics(router, store);

                // create the app instance.
                // here we inject the router, store and ssr context to all child components,
                // making them available everywhere as `this.$router` and `this.$store`.
                const app = new Vue({
                    router,
                    store,
                    vuetify,
                    i18n,
                    render: h => h(App),
                    metaInfo: {},
                });

                // sync the router with the vuex store.
                // this registers `store.state.route`
                sync(store, router);

                router.onError(error => console.log('router.afterEach error', error));

                router.afterEach(to => {
                    // if referral data query params presents, redirect to registration hash route with removing query
                    if (!to.hash && (to.query[referralDataTypes.referralLinkCode] || to.query[referralDataTypes.affId] || to.query[referralDataTypes.btag] || to.query[referralDataTypes.stag])) {
                        router.push(to.path + hashRoutes.registrationDialog).catch(error => console.log('router.afterEach push error', error));
                    }
                });

                router.beforeResolve((to, from, next) => {
                    let appLocale;

                    if (store.state.appLocale) {
                        appLocale = store.state.appLocale;
                    } else {
                        let routeLocale = to.params.locale || defaultLanguage.toLowerCase();
                        appLocale = appLocales.find(appLocale => appLocale.name.toLowerCase() === routeLocale);

                        store.commit('SET_APP_LOCALE', appLocale);
                        store.commit('SET_LOCALE', appLocale.name);
                        store.commit('SET_FALLBACK_LOCALES', appLocale.fallbackLocales);
                        store.commit('SET_RTL', appLocale.isRtl);
                    }

                    i18n.locale = appLocale.name;
                    i18n.fallbackLocale = appLocale.fallbackLocales;

                    // collect route promises
                    let routeLoadingDataPromises = [];

                    if (!store.state.maintenanceMode) {
                        // fetch layout parts only for first route
                        if (!store.state.layoutParts && !store.state.errorMode) {
                            routeLoadingDataPromises.push(store.dispatch('FETCH_LAYOUT_PARTS', addPropertiesToApiData({}, store.state)));
                        }

                        // fetch and set necessary translation messages
                        if (!i18n.availableLocales.includes(appLocale.name)) {
                            let translationLanguages = [appLocale.name];

                            if (appLocale.loadFallbackLocales) {
                                translationLanguages = translationLanguages.concat(appLocale.fallbackLocales);
                            }

                            let translationScopes = ['FRONT_BASE'];

                            if (store.state.user) {
                                translationScopes.push('FRONT_USER');
                            }

                            routeLoadingDataPromises.push(
                                store.dispatch('FETCH_TRANSLATION_MESSAGES', addPropertiesToApiData({
                                    locales: translationLanguages,
                                    scopes: translationScopes,
                                }, store.state))
                                    .then(messages => {
                                        return new Promise(resolve => {
                                            Object.keys(messages).forEach(locale => i18n.setLocaleMessage(locale, messages[locale]));
                                            resolve();
                                        })
                                    })
                            );
                        }

                        // fetch page data (only dynamic pages that route has backUrlTemplate)
                        if (to.meta.backUrlTemplate && !store.state.errorMode) {
                            let apiData = addPropertiesToApiData(Object.assign({}, to.params), store.state);

                            routeLoadingDataPromises.push(store.dispatch('FETCH_PAGE_DATA', {
                                apiData: apiData,
                                route: to
                            }));
                        }
                    }

                    // run promises if route not special redirect component 'serviceUnavailable'
                    if (routeLoadingDataPromises.length && to.name !== routesNames.SERVICE_UNAVAILABLE && to.name !== routesNames.PAGE_NOT_FOUND) {
                        Promise.all(routeLoadingDataPromises)
                            .then(() => {
                                if (to.meta.backUrlTemplate && !store.state.errorMode) {
                                    let pageData = store.getters.getPageData(to);
                                    let allowedLocales = pageData && pageData.page && pageData.page.allowedLocales;

                                    if (!Array.isArray(allowedLocales) || allowedLocales.includes(appLocale.name)) {
                                        // current locale in allowed array of received page data
                                        next();
                                    } else {
                                        if (allowedLocales.length) {
                                            // store existing allowed locales and route
                                            store.commit('SET_TRANSLATED_PAGE_ROUTES', {allowedLocales, route: to});

                                            // redirect to special static route 'noTranslation' for user can cast translated route
                                            next({
                                                name: routesNames.PAGE_HAS_NOT_TRANSLATION,
                                                params: {locale: appLocale.name}
                                            });
                                        } else {
                                            // no one translated roure for this page
                                            if (context) {
                                                let routeError = new Error(`No one available locale on route: ${to.fullPath}`);
                                                routeError.code = 404
                                                routeError.locale = to.params.locale;

                                                next(routeError);
                                            } else {
                                                next({
                                                    name: routesNames.PAGE_NOT_FOUND,
                                                    params: {locale: appLocale.name}
                                                });
                                            }
                                        }
                                    }
                                } else {
                                    next();
                                }
                            })
                            .catch(error => {
                                let status = error && error.response && error.response.status;
                                let errorResponseData = error.response && error.response.data;

                                let maintenanceMode = errorResponseData && errorResponseData.maintenanceMode;

                                if (status === 423 && maintenanceMode) {
                                    // set maintenance mode
                                    store.commit('SET_MAINTENANCE_MODE', maintenanceMode);
                                    next();
                                } else if (status === 429) {
                                    // too many requests from rate limiter
                                    // disable go to route
                                    // show message in AppContent by event from eventBus
                                    next(false);
                                } else {
                                    let errorCode = status === 404 || status === 301 ? status : 503;

                                    if (context) {
                                        let routeError = new Error(`Vue router before resolve promises return status code: ${status} on route: ${to.fullPath}`);

                                        routeError.code = errorCode;
                                        routeError.locale = to.params.locale;

                                        if (store.state.errorMode) {
                                            routeError.errorMode = true;
                                        }

                                        if (status === 301) {
                                            routeError.url = createRedirectUrl(router, to, errorResponseData);
                                            routeError.redirectStatus = 301;
                                        }

                                        next(routeError);
                                    } else {
                                        if (status === 301) {
                                            next(createRedirectUrl(router, to, errorResponseData));
                                        } else {
                                            if (errorCode === 404) {
                                                next({
                                                    name: routesNames.PAGE_NOT_FOUND,
                                                    params: {locale: appLocale.name}
                                                });
                                            } else {
                                                next({
                                                    name: routesNames.SERVICE_UNAVAILABLE,
                                                    params: {locale: appLocale.name}
                                                });
                                            }
                                        }
                                    }
                                }
                            });
                    } else {
                        next();
                    }
                });

                resolve({app, router, store});
            })
            .catch(error => reject(error));
    });
}

function prepareInitialState(context, store) {
    return new Promise((resolve, reject) => {
        // only for server side
        if (context) {
            const {
                cookies,
                ip,
                userAgent,
                isDeviseMobile,
                routes,
                geoData,
                maintenanceMode,
                errorMode,
                referralData,
            } = context;

            store.commit('SET_REFERRAL_DATA', referralData);

            store.commit('SET_REQUEST_IP', ip);

            store.commit('SET_REQUEST_USER_AGENT', userAgent);

            store.commit('SET_IS_DEVICE_MOBILE', isDeviseMobile);

            store.commit('SET_ROUTES', routes);

            if (geoData) {
                store.commit('SET_GUEST_CURRENCY', geoData.currencyCode);
                store.commit('SET_GUEST_COUNTRY', geoData.countryCode);
            }

            if (errorMode) {
                store.commit('SET_ERROR_MODE', errorMode);

                resolve();
            } else if (maintenanceMode) {
                store.commit('SET_MAINTENANCE_MODE', maintenanceMode);

                resolve();
            } else if (cookies[cookieNames.sessionKey]) {
                store.dispatch('FETCH_USER', addPropertiesToApiData({
                    sessionKey: cookies[cookieNames.sessionKey],
                }, store.state))
                    .then(() => resolve())
                    .catch(error => {
                        let status = error && error.response && error.response.status;
                        let maintenanceMode = error.response && error.response.data && error.response.data.maintenanceMode;

                        if (status === 419 || status === 422) {
                            store.commit('SET_USER', false);
                        } else {
                            store.commit('SET_USER', null);
                        }

                        if (status === 423 && maintenanceMode) {
                            store.commit('SET_MAINTENANCE_MODE', maintenanceMode);
                        }

                        resolve();
                    });
            } else {
                resolve();
            }
        } else if (typeof window !== 'undefined' && window.__INITIAL_STATE__) {
            // prime the store with server-initialized state.
            // the state is determined during SSR and inlined in the page markup.
            store.replaceState(window.__INITIAL_STATE__);

            // remove user cookie if user is null after fetch on server side
            if (Vue.$cookies.isKey(cookieNames.sessionKey) && store.state.user === false) {
                Vue.$cookies.remove(cookieNames.sessionKey);
                store.commit('SET_USER', null);
            }

            if (Vue.$cookies.isKey(cookieNames.guestToken)) {
                store.commit('SET_GUEST_TOKEN', Vue.$cookies.get(cookieNames.guestToken));
            }

            if (isWebpSupported()) {
                store.commit('SET_WEBP_SUPPORT', true);
            }

            // save to localStorage for use in browser's incognito mode
            saveReferralDataToLocalStorage(store);

            resolve();
        } else {
            reject();
        }
    })
}

function isWebpSupported() {
    let elem = document.createElement('canvas');

    if (!!(elem.getContext && elem.getContext('2d'))) {
        return elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } else {
        return false;
    }
}

function saveReferralDataToLocalStorage(store) {
    let storeReferralData = store.state.referralData;

    if (storeReferralData && Object.keys(storeReferralData).length) {
        let localStorageReferralData = JSON.parse(window.localStorage.getItem('referralData')) || {};

        let referralDataCreatedAt = (new Date()).getTime();

        Object.keys(storeReferralData).forEach(referralDataType => {
            localStorageReferralData[referralDataType] = {
                value: storeReferralData[referralDataType],
                createdAt: referralDataCreatedAt,
            }
        });

        window.localStorage.setItem('referralData', JSON.stringify(localStorageReferralData));
    }
}

function createRedirectUrl(router, currentRoute, errorResponseData) {
    let locale = currentRoute.params.locale;

    let routeParams = Object.assign(currentRoute.params, errorResponseData.redirectRouteParameters);

    routeParams.locale = locale === defaultLanguage ? null : locale;

    let redirectRoute = router.resolve({
        name: currentRoute.name,
        params: routeParams,
        query: currentRoute.query,
        hash: currentRoute.hash,
    });

    return decodeURIComponent(redirectRoute.resolved.fullPath);
}